import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap, of, catchError, throwError, BehaviorSubject, Subject,map } from 'rxjs';
import { BaseRestApiService } from 'src/app/service/baseRestApiService.service';
import { UrlProviderService, EndPoints } from 'src/app/service/urlprovider.service';
import { Node } from 'src/app/component/node/model/node.model';

@Injectable({
    providedIn: 'root'
})
export class WorkflowsService extends BaseRestApiService {
    private refreshTrigger = new Subject<void>();
    refresh$ = this.refreshTrigger.asObservable();
    constructor(http: HttpClient, urlProvider: UrlProviderService) {
        super(http, urlProvider);
    }

    index(): Observable<any> {
        return this.get<any>(EndPoints.workflows_Index).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getWorkflowStates(workflowsId: number): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetWorkflowStates, null, workflowsId).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    show(workflowsId: number): Observable<any> {
        return this.get<any>(EndPoints.workflows_Show, null, workflowsId).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    update(payload: any): Observable<any> {
        return this.post<any>(EndPoints.workflows_Update, false, null, { editWorkflow: payload.editWorkflow }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getWorkflowNodeTypes(): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetWorkflowNodeTypes, null, null).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getWorkflowTransitions(workflowsId: number): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetWorkflowTransitions, null, workflowsId).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getWorkflowRoles(workflowsId: number): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetWorkflowRoles, null, workflowsId).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getMyWorkflows(): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetMyWorkflows, null, null).pipe(
            map((result) => {
                const cloned = JSON.parse(JSON.stringify(result));

                const filterOutId6 = (arr: any[]) => arr.filter((item: any) => item?.ID !== 6);

                // If the API returns an array, filter it directly
                if (Array.isArray(cloned)) {
                    return filterOutId6(cloned);
                }

                // If the API returns an object containing an array, filter any array properties
                if (cloned && typeof cloned === 'object') {
                    const resultClone: any = { ...cloned };

                    Object.keys(resultClone).forEach((key) => {
                        if (Array.isArray(resultClone[key])) {
                            resultClone[key] = filterOutId6(resultClone[key]);
                        }
                    });

                    return resultClone;
                }

                return cloned;
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }
    getInboxStates(workflowsId: number): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetInboxStates, false, '',
            {
                Id: workflowsId
            }).pipe(
                switchMap((result) => {
                    return of(JSON.parse(JSON.stringify(result)));
                }),
                catchError((err) => {
                    return throwError(() => new Error(err));
                })
            );
    }

    getInProgressStates(workflowsId: number): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetInProgressStates, false, '',
            {
                Id: workflowsId
            }).pipe(
                switchMap((result) => {
                    return of(JSON.parse(JSON.stringify(result)));
                }),
                catchError((err) => {
                    return throwError(() => new Error(err));
                })
            );
    }

    getArchiveStates(workflowsId: number): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetArchiveStates, false, '',
            {
                Id: workflowsId
            }).pipe(
                switchMap((result) => {
                    return of(JSON.parse(JSON.stringify(result)));
                }),
                catchError((err) => {
                    return throwError(() => new Error(err));
                })
            );
    }

    getOutboxStates(workflowsId: number): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetOutboxStates, false, '',
            {
                Id: workflowsId
            }).pipe(
                switchMap((result) => {
                    return of(JSON.parse(JSON.stringify(result)));
                }),
                catchError((err) => {
                    return throwError(() => new Error(err));
                })
            );
    }

    getWorkflowStateNodes(payload: any): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetWorkflowStateNodes, false, '',
            {
                Id: payload.id,
                chosenStateNames: payload.chosenStateNames,
                coeNumber: payload.coeNumber,
                fromDateString: payload.fromDateString,
                toDateString: payload.toDateString
            }).pipe(
                switchMap((result) => {
                    return of(JSON.parse(JSON.stringify(result)));
                }),
                catchError((err) => {
                    return throwError(() => new Error(err));
                })
            );
    }

    getCreateInProgressStatus(): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetCreateInProgressStatus, null, null).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getInboxNodesCount(): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetInboxNodesCount, false, '',
        ).pipe(
            switchMap((result) => {
                return of(JSON.parse(JSON.stringify(result)));
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getInProgressNodesCount(): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetInProgressNodesCount, false, '',
        ).pipe(
            switchMap((result) => {
                return of(JSON.parse(JSON.stringify(result)));
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getOutboxNodesCount(): Observable<any> {
        return this.post<any>(EndPoints.workflows_GetOutboxNodesCount, false, '',
        ).pipe(
            switchMap((result) => {
                return of(JSON.parse(JSON.stringify(result)));
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getMyTransitions(NodeId: number): Observable<any> {
        return this.get<any>(EndPoints.workflows_GetMyTransitions, null, NodeId).pipe(
            switchMap((result) => {
                return of(JSON.parse(JSON.stringify(result)));
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getWorkFlowStateTypesByStateNameWorkflowName(stateName: string, workflowName: string, node: Node): Observable<any> {
         return this.post<any>(EndPoints.workflows_GetWorkFlowStateTypesByStateNameWorkflowName, false, '',
         {
            StateName: stateName, WorkflowName: workflowName, node: node
         }).pipe(
            switchMap((result) => {
                console.log("result", JSON.stringify("result", result));
                 return of(JSON.parse(JSON.stringify(result)));
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    } 
    triggerRefreshWorkbenchCounts(){
		this.refreshTrigger.next();
	}
}
